import {useEffect, useMemo, useState} from "react";
import { Stack, IconButton } from "@fluentui/react";
import DOMPurify from "dompurify";

import styles from "./Answer.module.css";

import {AskResponse, getContentMetadata, getSeismicSearchData} from "../../api";
import { parseAnswerToHtml } from "./AnswerParser";
import { AnswerIcon } from "./AnswerIcon";
import { Tooltip } from "../Tooltip";

interface Props {
    answer: AskResponse;
    isSelected?: boolean;
    onCitationClicked: (filePath: string) => void;
    onThoughtProcessClicked: () => void;
    onSupportingContentClicked: () => void;
    onFollowupQuestionClicked?: (question: string) => void;
    showFollowupQuestions?: boolean;
}

const KnowledgeReference = ({x, i} :any) => {

    const getPrettyName = (name :any) => {
        try {
            let dashIndex = name.indexOf("-");
            //remove everything before and including the dash as well as 5 chars at the end
            let encodedUrl = name.substring(dashIndex + 1, name.length - 5);
            //console.log("encodedUrl", encodedUrl);
            let encoded = encodedUrl.replaceAll('-', "+").replace('_', '/');
            //console.log("encoded", encoded);
            //b64 decode
            let decoded = atob(encoded);
            //console.log("decoded", decoded);
            return decoded;
        }
        catch(e) {
            console.error(e);
            return null;
        }
    }

    const getLink = (name :any) => {
        try {
            return `https://quake.knowledge.seismic.com/content/${name.split('-')[0]}`;
        }
        catch(e) {
            console.error(e);
            return null;
        }
    }

    const prettyName = getPrettyName(x);
    const link = getLink(x);
    return (
        prettyName != null && link != null ? <a key={i} className={styles.citation} title={prettyName} onClick={() => window.open(link)}>
            {`${++i}. ${prettyName}`}
        </a> : <QuestionableReference x={x} i={i} />
    );
}

const ContentTooltip = ({content} :any) => {

    return <div style={{fontFamily: '"Segoe UI", "Segoe UI Web (West European)", "Segoe UI", -apple-system, BlinkMacSystemFont, Roboto, "Helvetica Neue", sans-serif', fontSize: "14px", width: "600px", backgroundColor: "white", border: "1px solid #f3f4f6", borderRadius: "8px"}}>
        <div style={{padding: "16px"}}>
            <div style={{fontWeight: "800"}}>{content.name} (Format={content.format} Page={content.page} Chunk={content.chunk})</div>
            <hr />
            <div>{content.content}</div>
        </div>
    </div>
}

const SeismicSearchReference = ({x, i} :any) => {

    const [badReference, setBadReference] = useState(false);
    const [content, setContent] = useState<any>(null);

    useEffect(() => {

        let id = x.replace('.ss', '');

        //console.log("id", id)

        if(id.length !== 36) {
            setBadReference(true);
            return;
        }

        setBadReference(false);
        getSeismicSearchData(id).then((response) => {
            //console.log("response", response)
            if(response.message){
                setBadReference(true);
                return;
            }
            setContent(response);
        })
    }, [x]);

    if(badReference)
        return <QuestionableReference x={x} i={i} />;

    return (
        content && content.link ?
            <Tooltip direction="right" content={<ContentTooltip content={content} />}>
                <a key={i} className={styles.citation} onClick={() => window.open(content.link)}>
                    {`${++i}. ${content.name}`}
                </a>
            </Tooltip>
            : <div>Loading...</div>
    );
}

const ContentReference = ({x, i} :any) => {
    
    const [badReference, setBadReference] = useState(false);
    const [content, setContent] = useState<any>(null);
    
    useEffect(() => {
        
        let id = x.replace('.json', '');
        
        //console.log("id", id)
        
        if(id.length < 40) {
            setBadReference(true);
            return;
        }

        setBadReference(false);
        getContentMetadata(id).then((response) => {
            //console.log("response", response)
            if(response.message){
                setBadReference(true);
                return;
            }
            setContent(response);
        })
    }, [x]);
    
    if(badReference)
        return <QuestionableReference x={x} i={i} />;
    
    return (
        content && content.libraryContentVersionId ?
        <Tooltip direction="right" content={<ContentTooltip content={content} />}>
            <a key={i} className={styles.citation} onClick={() => window.open(content.link)}>
            {`${++i}. ${content.name}${(content.page === 0 ? "" : " (Page " + content.page + ")")}`}
            </a>
        </Tooltip>
            : <div>Loading...</div>
    );
}

const QuestionableReference = ({x, i} :any) => {
    return <div className={styles.citation + " " + styles.questionable} title={x}>${++i}. Invalid Reference Provided</div>
}

const SnippetsReference = ({x, i} :any) => {
    return <a key={i} href={x.url} target="_blank" className={styles.citation}>
        {x.title}
    </a>
}

const Reference = ({x, i} :any) => {

    if(x != null && x.endsWith(".html")) { //knowledge
        return <KnowledgeReference x={x} i={i} />;
    }

    if(x != null && x.endsWith(".ss")) { //knowledge
        return <SeismicSearchReference x={x} i={i} />;
    }

    if(x != null && x.endsWith(".json")) { //content
        return <ContentReference x={x} i={i} />;
    }

    if(x != null && x.startsWith("https")) { //content
        return <SnippetsReference x={x} i={i} />;
    }
    
    return <QuestionableReference x={x} i={i} />;
}

export const Answer = ({
    answer,
    isSelected,
    onCitationClicked,
    onThoughtProcessClicked,
    onSupportingContentClicked,
    onFollowupQuestionClicked,
    showFollowupQuestions
}: Props) => {

    console.log(answer['sources'])

    const parsedAnswer = useMemo(() => parseAnswerToHtml(answer.answer, onCitationClicked), [answer]);

    const sanitizedAnswerHtml = DOMPurify.sanitize(parsedAnswer.answerHtml);

    return (
        <Stack className={`${styles.answerContainer} ${isSelected && styles.selected}`} verticalAlign="space-between">
            <Stack.Item>
                <Stack horizontal horizontalAlign="space-between">
                    <AnswerIcon />
                    <div style={{display: "none"}}>
                        <IconButton
                            style={{ color: "black" }}
                            iconProps={{ iconName: "Lightbulb" }}
                            title="Show thought process"
                            ariaLabel="Show thought process"
                            onClick={() => onThoughtProcessClicked()}
                            disabled={!answer.thoughts}
                        />
                        <IconButton
                            style={{ color: "black" }}
                            iconProps={{ iconName: "ClipboardList" }}
                            title="Show supporting content"
                            ariaLabel="Show supporting content"
                            onClick={() => onSupportingContentClicked()}
                            disabled={!answer.data_points.length}
                        />
                    </div>
                </Stack>
            </Stack.Item>

            <Stack.Item grow>
                <div className={styles.answerText} dangerouslySetInnerHTML={{ __html: sanitizedAnswerHtml }}></div>
            </Stack.Item>

            {!!parsedAnswer.citations.length && (
                <Stack.Item>
                    <Stack horizontal wrap tokens={{ childrenGap: 5 }}>
                        <span className={styles.citationLearnMore}>References:</span>
                        {parsedAnswer.citations.map((x, i) =>
                            <Reference x={x} i={i} />
                        )}
                    </Stack>
                </Stack.Item>
            )}

            {answer && answer['sources'] && answer['sources'].length && (
                <Stack.Item>
                    <Stack horizontal wrap tokens={{ childrenGap: 5 }}>
                        <span className={styles.citationLearnMore}>References:</span>
                        {answer['sources'].map((x, i) =>
                            <>
                            <SnippetsReference x={x} i={i} /><br/>
                            </>
                        )}
                    </Stack>
                </Stack.Item>
            )}

            {!!parsedAnswer.followupQuestions.length && showFollowupQuestions && onFollowupQuestionClicked && (
                <Stack.Item>
                    <Stack horizontal wrap className={`${!!parsedAnswer.citations.length ? styles.followupQuestionsList : ""}`} tokens={{ childrenGap: 6 }}>
                        <span className={styles.followupQuestionLearnMore}>Follow-up questions:</span>
                        {parsedAnswer.followupQuestions.map((x, i) => {
                            return (
                                <a key={i} className={styles.followupQuestion} title={x} onClick={() => onFollowupQuestionClicked(x)}>
                                    {`${x}`}
                                </a>
                            );
                        })}
                    </Stack>
                </Stack.Item>
            )}
        </Stack>
    );
};
